import React from 'react';
import { Layout } from '../../components/layout/Layout';
import { ScrollAnimation } from '../../components/common/ScrollAnimation';
import { Button } from '../../components/ui/Button';
import { Link } from 'react-router-dom';
import { Heart, Camera, Sparkles, Star, Clock, Award } from 'lucide-react';
import { motion } from 'framer-motion';

const services = [
  {
    title: 'Newborn Shoots',
    description: 'Delicate, safe newborn photography within the first 14 days — the sleepiest, sweetest moments.',
    icon: Heart,
    gradient: 'from-rose-400 to-pink-500',
    shadow: 'shadow-rose-400/30',
    bg: 'from-rose-50 to-pink-50',
    border: 'border-rose-200',
    image: 'https://images.unsplash.com/photo-1544126592-807ade215a0b?auto=format&fit=crop&w=500&q=80',
    emoji: '👶',
  },
  {
    title: 'Baby Shower',
    description: 'Capture the joy, love, and excitement of your baby shower celebration with family.',
    icon: Sparkles,
    gradient: 'from-amber-400 to-orange-500',
    shadow: 'shadow-amber-400/30',
    bg: 'from-amber-50 to-orange-50',
    border: 'border-amber-200',
    image: 'https://images.unsplash.com/photo-1438962136829-452260720431?auto=format&fit=crop&w=500&q=80',
    emoji: '🎀',
  },
  {
    title: 'Baby Bumps',
    description: 'Stunning maternity photography celebrating the beauty of motherhood and new life.',
    icon: Heart,
    gradient: 'from-violet-400 to-purple-500',
    shadow: 'shadow-violet-400/30',
    bg: 'from-violet-50 to-purple-50',
    border: 'border-violet-200',
    image: 'https://images.unsplash.com/photo-1493894473891-10fc1e5dbd22?auto=format&fit=crop&w=500&q=80',
    emoji: '🤰',
  },
  {
    title: 'Toddler Photography',
    description: 'Playful, candid sessions capturing your toddler\'s curiosity, giggles, and endless energy.',
    icon: Camera,
    gradient: 'from-emerald-400 to-teal-500',
    shadow: 'shadow-emerald-400/30',
    bg: 'from-emerald-50 to-teal-50',
    border: 'border-emerald-200',
    image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=500&q=80',
    emoji: '🧸',
  },
];

const whyUs = [
  { icon: Heart, title: 'Safe & Gentle', desc: 'Specially trained for newborn safety — soft posing, temperature-controlled studio.', gradient: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-400/30' },
  { icon: Clock, title: 'Patient & Flexible', desc: 'Sessions are baby-led. We never rush — every yawn and stretch gets captured.', gradient: 'from-amber-500 to-orange-600', shadow: 'shadow-amber-400/30' },
  { icon: Camera, title: 'Professional Studio', desc: 'Fully equipped studio with themed props, backdrops, and soft-box lighting.', gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-400/30' },
  { icon: Award, title: 'Premium Prints', desc: 'Beautiful edited photos delivered digitally plus premium print packages available.', gradient: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-400/30' },
];

const galleryImages = [
  { url: 'https://images.unsplash.com/photo-1522771930-78848d9293e8?auto=format&fit=crop&w=400&q=80', label: 'Cosy Cub', gradient: 'from-rose-400 to-pink-500' },
  { url: 'https://images.unsplash.com/photo-1491013516836-7db643ee125a?auto=format&fit=crop&w=400&q=80', label: 'Sweet Baby', gradient: 'from-amber-400 to-orange-500' },
  { url: 'https://images.unsplash.com/photo-1555252333-9f8e92e65df9?auto=format&fit=crop&w=400&q=80', label: 'Tiny Toes', gradient: 'from-violet-400 to-purple-500' },
  { url: 'https://images.unsplash.com/photo-1566004100631-35d015d6a491?auto=format&fit=crop&w=400&q=80', label: 'Peekaboo', gradient: 'from-emerald-400 to-teal-500' },
  { url: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=400&q=80', label: 'Toddler Joy', gradient: 'from-cyan-400 to-blue-500' },
  { url: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?auto=format&fit=crop&w=400&q=80', label: 'Toy Time', gradient: 'from-fuchsia-400 to-pink-500' },
  { url: 'https://images.unsplash.com/photo-1504439268584-b72c5019471e?auto=format&fit=crop&w=400&q=80', label: 'Tiny Hands', gradient: 'from-rose-400 to-red-500' },
  { url: 'https://images.unsplash.com/photo-1531983412531-1f49a365ffed?auto=format&fit=crop&w=400&q=80', label: "Mother's Love", gradient: 'from-violet-400 to-indigo-500' },
];

export const BabyPhotographyPage: React.FC = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="relative text-white py-28 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #881337 0%, #9d174d 30%, #701a75 60%, #86198f 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-10 left-16 w-72 h-72 rounded-full blur-3xl opacity-30"
            style={{ background: '#fda4af' }}
            animate={{ x: [0, 50, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-10 right-16 w-80 h-80 rounded-full blur-3xl opacity-25"
            style={{ background: '#f0abfc' }}
            animate={{ x: [0, -40, 0], y: [0, -25, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl opacity-15"
            style={{ background: '#fcd34d' }}
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
        <div className="h-1.5 absolute top-0 left-0 right-0 bg-gradient-to-r from-rose-300 via-amber-300 via-violet-300 to-pink-300" />

        {/* Floating elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {['🌸', '⭐', '🌷', '💫', '🎀', '✨'].map((emoji, i) => (
            <motion.div
              key={i}
              className="absolute text-3xl opacity-20"
              style={{
                top: `${10 + (i * 15)}%`,
                left: i % 2 === 0 ? `${5 + i * 3}%` : undefined,
                right: i % 2 !== 0 ? `${5 + i * 2}%` : undefined,
              }}
              animate={{
                y: [0, -20, 0],
                rotate: [0, 15, -15, 0],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.3,
              }}
            >
              {emoji}
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={false}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-6 border border-rose-300/50"
              style={{ background: 'rgba(253, 164, 175, 0.15)', color: '#fda4af' }}
            >
              <Heart className="h-4 w-4 fill-current" /> Precious Little Moments
            </motion.div>
            <h1 className="text-4xl md:text-7xl font-bold mb-6 leading-tight">
              Baby{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #fda4af, #fcd34d, #c4b5fd)' }}>
                Photography
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-pink-100 max-w-3xl mx-auto leading-relaxed">
              Every baby is a miracle. Let us preserve those fleeting, irreplaceable moments —
              from tiny toes to first smiles — with the love and care they deserve.
            </p>
            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/contact">
                <Button variant="secondary" size="lg" className="shadow-xl shadow-amber-500/30 hover:scale-105 transition-transform">
                  Book a Session
                </Button>
              </Link>
              <Link to="/gallery">
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/15 hover:scale-105 transition-transform backdrop-blur-sm">
                  See Our Work
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services with images */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #fff1f2 0%, #fdf4ff 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 gradient-text">Our Baby Photography Services</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Tailored sessions designed for every stage of your little one's early life
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, index) => (
              <ScrollAnimation key={s.title} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -15, scale: 1.03 }}
                  className={`bg-gradient-to-br ${s.bg} border ${s.border} rounded-2xl shadow-lg ${s.shadow} hover:shadow-2xl transition-all overflow-hidden`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${s.gradient} hidden items-center justify-center`}
                    >
                      <span className="text-6xl">{s.emoji}</span>
                    </div>
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/40 to-transparent`} />
                    <div className={`absolute top-3 right-3 bg-gradient-to-br ${s.gradient} rounded-xl p-2.5 shadow-lg ${s.shadow}`}>
                      <s.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="absolute bottom-3 left-3 text-3xl">{s.emoji}</div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold mb-2 text-gray-800">{s.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{s.description}</p>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Colorful divider */}
      <div className="h-2 w-full bg-gradient-to-r from-rose-400 via-amber-400 via-violet-400 to-emerald-400" />

      {/* Gallery */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 50%, #fff1f2 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 gradient-text">Gallery of Tiny Wonders</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              A peek into the magical world we capture for every family
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <ScrollAnimation key={index} direction="up" delay={index * 0.07}>
                <motion.div
                  whileHover={{ scale: 1.07, rotate: index % 2 === 0 ? 2 : -2 }}
                  className="aspect-square rounded-2xl overflow-hidden shadow-xl cursor-pointer group relative"
                >
                  <img
                    src={img.url}
                    alt={img.label}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  {/* Gradient fallback */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${img.gradient} hidden items-center justify-center`}>
                    <span className="text-5xl">👶</span>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <p className="text-white text-xs font-semibold p-3">{img.label}</p>
                  </div>
                  {/* Heart on hover */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center shadow-md">
                      <Heart className="h-3.5 w-3.5 text-rose-500 fill-rose-500" />
                    </div>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/gallery">
              <Button variant="primary" size="lg" className="shadow-lg shadow-primary-500/30">
                View Full Gallery
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #fdf4ff 50%, #fff7ed 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 gradient-text">Why Parents Choose Us</h2>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.map((item, index) => (
              <ScrollAnimation key={item.title} direction="up" delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -8 }}
                  className="text-center p-7 rounded-2xl bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-all"
                >
                  <div className={`bg-gradient-to-br ${item.gradient} rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center shadow-lg ${item.shadow}`}>
                    <item.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-gray-800">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #fff1f2 0%, #fae8ff 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 gradient-text">Happy Parents Say</h2>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Anjali & Pradeep', text: 'Our newborn shoot was absolutely magical. They handled our baby with so much love and care!', accent: 'from-rose-500 to-pink-600' },
              { name: 'Deepika Rajesh', text: 'The maternity photos are stunning. I\'ll treasure them for the rest of my life. Thank you AVC!', accent: 'from-violet-500 to-purple-600' },
              { name: 'Kavitha & Suresh', text: 'Our little one\'s 6-month shoot was so fun. The photos came out incredibly cute and vibrant!', accent: 'from-amber-500 to-orange-600' },
            ].map((t, i) => (
              <ScrollAnimation key={i} direction="up" delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -6 }}
                  className="bg-white rounded-2xl shadow-lg p-6 border border-pink-100 hover:shadow-xl transition-all"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic mb-4 text-sm leading-relaxed">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${t.accent} flex items-center justify-center text-white font-bold text-sm shadow`}>
                      {t.name[0]}
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #881337 0%, #9d174d 40%, #701a75 70%, #4c1d95 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20 -mr-48 -mt-48" style={{ background: '#fda4af' }} />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-15 -ml-48 -mb-48" style={{ background: '#f0abfc' }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <ScrollAnimation direction="fade">
            <div className="text-6xl mb-6">🍼</div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Book Your{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #fda4af, #fcd34d)' }}>
                Baby Session
              </span>
            </h2>
            <p className="text-xl text-pink-100 mb-8 max-w-2xl mx-auto">
              These tiny moments won't stay tiny forever. Let us capture them beautifully for you.
            </p>
            <Link to="/contact">
              <Button variant="secondary" size="lg" className="shadow-2xl shadow-amber-500/30 hover:scale-105 transition-transform">
                <Heart className="h-5 w-5 mr-2 fill-current" /> Book Now
              </Button>
            </Link>
          </ScrollAnimation>
        </div>
      </section>
    </Layout>
  );
};
